/**
 * The loading mark: the "oo" of the wordmark with the door terminal's boot
 * animation (cowoo-door `deploy/splash/cowoo.script`). A soft glare runs once
 * round the rings as an 8 — onto the right ring clockwise, across the middle,
 * round the left ring anticlockwise and back — while the lime dot fades out;
 * the dot fades in again once the 8 is complete, and the mark rests a moment.
 *
 * On the terminal the rings are white on teal and the glare is light on the
 * teal around them. The web shows it on white, so the rings are teal and the
 * glare is white light on the strokes, masked to them. Timing and path match
 * the splash; the keyframes live in the Tailwind preset (`cowoo-glare-*`,
 * `cowoo-dot`) and only run under `motion-safe:` — with reduced motion the
 * mark simply stands.
 */

import { useId, type CSSProperties } from 'react';

/**
 * The 8 through the crossing M (42, 24) of the rings (centres 23 and 61,
 * centre-line radius 17): up onto the right ring at 225°, clockwise to 135°,
 * across M onto the left ring at -45°, anticlockwise to 45°, back to M.
 */
const FIGURE_EIGHT = 'M42 24 L48.98 11.98 A17 17 0 1 1 48.98 36.02 L35.02 11.98 A17 17 0 1 0 35.02 36.02 Z';

/** The splash's motion trail, as a faint long tail behind a brighter head. */
const GLARE = [
  { dash: 24, opacity: 0.35, width: 13 },
  { dash: 9, opacity: 0.7, width: 11 },
] as const;

export function LoadingMark({ width = 88 }: { width?: number }) {
  const id = useId();
  const mask = `${id}-rings`;
  const blur = `${id}-blur`;
  return (
    <svg viewBox="0 0 84 48" width={width} height={(width * 48) / 84} aria-hidden className="block">
      <defs>
        <mask id={mask}>
          <circle cx="23" cy="24" r="17" fill="none" stroke="white" strokeWidth="11" />
          <circle cx="61" cy="24" r="17" fill="none" stroke="white" strokeWidth="11" />
        </mask>
        <filter id={blur} x="-20%" y="-40%" width="140%" height="180%">
          <feGaussianBlur stdDeviation="1.6" />
        </filter>
      </defs>
      <g className="stroke-teal">
        <circle cx="23" cy="24" r="17" fill="none" strokeWidth="11" />
        <circle cx="61" cy="24" r="17" fill="none" strokeWidth="11" />
      </g>
      <g mask={`url(#${mask})`}>
        {GLARE.map((glare) => (
          <path
            key={glare.dash}
            d={FIGURE_EIGHT}
            pathLength={100}
            fill="none"
            stroke="white"
            strokeWidth={glare.width}
            strokeLinecap="round"
            strokeDasharray={`${glare.dash} ${100 - glare.dash}`}
            filter={`url(#${blur})`}
            className="motion-safe:animate-cowoo-glare opacity-0"
            // In px: a unitless start and a `calc()` end do not interpolate, the
            // offset would jump; `pathLength` scales px like any user unit.
            style={{ '--dash': `${glare.dash}px`, '--glare': glare.opacity } as CSSProperties}
          />
        ))}
      </g>
      <circle cx="61" cy="24" r="6.5" className="fill-lime motion-safe:animate-cowoo-dot" />
    </svg>
  );
}

/** The page-sized loading state: the mark with the caption below it, announced politely. */
export function LoadingScreen({ label }: { label: string }) {
  return (
    <main
      role="status"
      aria-live="polite"
      className="flex min-h-[60vh] flex-col items-center justify-center gap-5 px-5"
    >
      <LoadingMark />
      <p className="font-body text-muted text-sm">{label}</p>
    </main>
  );
}
